import {
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ForwardIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

/**
 * Playback transport controls — shared by both dashboards' recordings player.
 * Fully presentational: renders the play/pause button, the jump cluster, and the
 * speed cluster, and reports intent through callbacks. State (isPlaying, rate,
 * seeking) lives in each page; this component only draws and dispatches.
 *
 * Layout keeps the three groups visually separate — a primary play button, then a
 * segmented jump pill (−30s / −10s / +10s / +30s), then a segmented speed pill
 * (slower / current rate / faster) — so the jump and fast-forward controls never
 * read as one undifferentiated row of buttons.
 */
interface PlaybackTransportProps {
  /** A stream is loaded (playbackUrl truthy). Gates the jump buttons and play-label. */
  hasStream: boolean;
  isPlaying: boolean;
  /** Disable the primary button (e.g. loading, or no segments in range). */
  playDisabled?: boolean;
  /** Play / pause / start-range — the page decides which based on hasStream. */
  onPlayPause: () => void;
  /** Seek the playhead by ±seconds within the continuous source. */
  onSkip: (deltaSec: number) => void;
  playbackRate: number;
  onSpeedUp: () => void;
  onSlowDown: () => void;
  /** Speed a single ↓ step lands on — used for the slow-down button's tooltip. */
  slowerSpeed?: number;
  className?: string;
}

const seg =
  'flex items-center justify-center px-2.5 py-1.5 text-[12px] font-mono text-foreground ' +
  'hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';

export default function PlaybackTransport({
  hasStream,
  isPlaying,
  playDisabled = false,
  onPlayPause,
  onSkip,
  playbackRate,
  onSpeedUp,
  onSlowDown,
  slowerSpeed,
  className = '',
}: PlaybackTransportProps) {
  return (
    /* Wraps rather than overflows: the three clusters need ~440px of width, and the bar they
       sit in is only as wide as the player column — narrower than that on a split-screen
       window, a phone, or after an external display resizes the viewport. Unwrapped, the
       speed pill (and sometimes the jump pill) slid past the edge of a clipped parent and
       vanished. */
    <div className={`flex flex-wrap items-center gap-2 gap-y-2 sm:gap-3 ${className}`}>
      {/* Primary: play / pause / start range */}
      <button
        onClick={onPlayPause}
        disabled={playDisabled}
        className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-brand-dark transition-colors disabled:opacity-50">
        {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
        {hasStream ? (isPlaying ? 'Pause' : 'Resume') : 'Play Range'}
      </button>

      {/* Jump cluster — segmented pill, reverse/forward by 10s and 30s */}
      <div className="inline-flex shrink-0 items-center rounded-lg border border-border overflow-hidden divide-x divide-border">
        <button onClick={() => onSkip(-30)} disabled={!hasStream} title="Back 30 seconds" className={seg}>
          <BackwardIcon className="h-3.5 w-3.5 mr-1" />30s
        </button>
        <button onClick={() => onSkip(-10)} disabled={!hasStream} title="Back 10 seconds" className={seg}>
          <BackwardIcon className="h-3.5 w-3.5 mr-1" />10s
        </button>
        <button onClick={() => onSkip(10)} disabled={!hasStream} title="Forward 10 seconds" className={seg}>
          10s<ForwardIcon className="h-3.5 w-3.5 ml-1" />
        </button>
        <button onClick={() => onSkip(30)} disabled={!hasStream} title="Forward 30 seconds" className={seg}>
          30s<ForwardIcon className="h-3.5 w-3.5 ml-1" />
        </button>
      </div>

      {/* Speed cluster — segmented pill: slower / current rate / faster */}
      <div className="inline-flex shrink-0 items-center rounded-lg border border-border overflow-hidden divide-x divide-border">
        <button
          onClick={onSlowDown}
          title={slowerSpeed != null ? `Slow down to ${slowerSpeed}×` : 'Slow down'}
          className={seg}>
          <MinusIcon className="h-3.5 w-3.5" />
        </button>
        <span className="px-3 py-1.5 text-[13px] font-mono font-semibold text-foreground tabular-nums select-none">
          {playbackRate}×
        </span>
        <button onClick={onSpeedUp} title="Speed up" className={seg}>
          <PlusIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
